import { Suspense, lazy, useEffect, useRef } from 'react';

import AccountDetailPage from '../pages/AccountDetailPage';
import AccountGeneralSettingsPage from '../pages/AccountGeneralSettingsPage';
import AccountNotificationSettingsPage from '../pages/AccountNotificationSettingsPage';
import AccountProfilePage from '../pages/AccountProfilePage';
import BinderDetailPage from '../pages/BinderDetailPage';
import BindersIndexPage from '../pages/BindersIndexPage';
import DashboardPage from '../pages/DashboardPage';
import ImportFilesPage from '../pages/ImportFilesPage';
import MemberDetailPage from '../pages/MemberDetailPage';
import MembersIndexPage from '../pages/MembersIndexPage';
import MobileNav from './MobileNav';
import OnsongImportPage from '../pages/OnsongImportPage';
import PageLoading from './PageLoading';
import PcoRedirectPage from '../pages/PcoRedirectPage';
import { Redirect, Route } from 'react-router-dom';
import type { RouteComponentProps } from 'react-router-dom';
import SearchPage from './SearchPage';
import SetlistDetailPage from '../pages/SetlistDetailPage';
import SetlistsIndexPage from '../pages/SetlistsIndexPage';
import NavigationRail from './NavigationRail';
import SongDetailPage from '../pages/SongDetailPage';
import ImportSongsPage from '../pages/ImportSongsPage';
import SongsIndexPage from '../pages/SongsIndexPage';
import TeamDetailPage from '../pages/TeamDetailPage';
import EventFormProvider from '../contexts/EventFormProvider';
import { useSelector } from 'react-redux';
import { selectCurrentMember } from '../store/authSlice';
import { MANAGE_BILLING } from '../utils/constants';
import AccountAppearancePage from '../pages/AccountAppearancePage';
import ImportCadenceSongsPage from '../pages/ImportCadenceSongsPage';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';

const PlanningCenterSongsPage = lazy(
  () => import('../pages/PlanningCenterSongsPage')
);
const RoleDetailPage = lazy(() => import('../pages/RoleDetailPage'));
const RolesIndexPage = lazy(() => import('../pages/RolesIndexPage'));
const CalendarPage = lazy(() => import('../pages/CalendarPage'));
const ChatPage = lazy(() => import('../pages/ChatPage'));
const EditCalendarEventPage = lazy(
  () => import('../pages/EditCalendarEventPage')
);
const CreateCalendarEventPage = lazy(
  () => import('../pages/CreateCalendarEventPage')
);
const BillingPage = lazy(() => import('../pages/BillingPage'));

export default function Content() {
  const currentMember = useSelector(selectCurrentMember);
  const location = useLocation();
  const isChat = location.pathname?.startsWith('/chat');
  const sheet = useRef<HTMLDivElement>(null);

  // From md the sheet scrolls, not the window, so a new page starts at its
  // top rather than wherever the last one was scrolled to.
  useEffect(() => {
    sheet.current?.scrollTo?.({ top: 0 });
  }, [location.pathname]);

  if (!currentMember) return null;

  return (
    <>
      {/* From md: the screen on surface-container-low (the rail sits on it)
          and the page in a rounded sheet on surface-container-lowest, 12dp in
          from the edges. The sheet scrolls itself, so its corners stay put.
          It's sized by margins and height, not fixed: a fixed sheet would be
          its own stacking context and cover the rail's menus. */}
      <div
        aria-hidden="true"
        className="fixed inset-0 hidden -z-10 md:block bg-surface-container-low"
      />
      <NavigationRail />
      <MobileNav />
      <div
        ref={sheet}
        // The page's scroller from md, which the quick-add FAB follows.
        data-page-scroller
        className={classNames(
          !isChat && 'p-3 md:px-10 md:py-8',
          'md:ml-24 lg:ml-[220px] md:my-3 md:mr-3 md:h-[calc(100vh-24px)] md:overflow-y-auto md:rounded-extra-large md:bg-surface-container-lowest'
        )}
      >
        <div className={classNames(!isChat && 'container', 'mx-auto')}>
          <Route path="/" exact>
            <DashboardPage />
          </Route>
          {/* Binders are called folders in the UI. Old /binders links
              redirect. */}
          <Route path="/folders/:id" exact>
            <BinderDetailPage />
          </Route>
          <Route path="/folders" exact>
            <BindersIndexPage />
          </Route>
          <Route
            path="/binders/:id"
            exact
            render={({ match }: RouteComponentProps<{ id: string }>) => (
              <Redirect to={`/folders/${match.params.id}`} />
            )}
          />
          <Route path="/binders" exact>
            <Redirect to="/folders" />
          </Route>
          <Route path="/import/files" exact>
            <ImportFilesPage />
          </Route>
          <Route path="/import/onsong" exact>
            <OnsongImportPage />
          </Route>
          <Suspense fallback={<PageLoading />}>
            <Route
              path="/import/planning-center"
              exact
              component={PlanningCenterSongsPage}
            />
          </Suspense>
          <Route path="/import/cadence" exact>
            <ImportCadenceSongsPage />
          </Route>
          <Route path="/import/pco_redirect" exact>
            <PcoRedirectPage />
          </Route>
          <Route path="/import" exact>
            <ImportSongsPage />
          </Route>
          <Route path="/songs/:id" exact>
            <SongDetailPage />
          </Route>
          <Route path="/songs" exact>
            <SongsIndexPage />
          </Route>
          <Route path="/sets" exact>
            <SetlistsIndexPage />
          </Route>
          <Route path="/sets/:id" exact>
            <SetlistDetailPage />
          </Route>
          <Route path="/account" exact>
            <AccountDetailPage />
          </Route>
          <Route path="/account/settings" exact>
            <AccountGeneralSettingsPage />
          </Route>
          <Route path="/account/appearance" exact>
            <AccountAppearancePage />
          </Route>
          <Route path="/account/profile" exact>
            <AccountProfilePage />
          </Route>
          <Route path="/account/notifications" exact>
            <AccountNotificationSettingsPage />
          </Route>
          <Route path="/members/:id" exact>
            <MemberDetailPage />
          </Route>
          <Route path="/members" exact>
            <MembersIndexPage />
          </Route>
          <Route path="/team">
            <TeamDetailPage />
          </Route>
          <Route path="/search">
            <SearchPage />
          </Route>
          <Suspense fallback={<PageLoading />}>
            <Route path="/permissions/:id" exact component={RoleDetailPage} />
          </Suspense>
          <Suspense fallback={<PageLoading />}>
            <Route path="/permissions" exact component={RolesIndexPage} />
          </Suspense>
          {currentMember.can(MANAGE_BILLING) && (
            <Suspense
              fallback={<PageLoading>Loading billing details</PageLoading>}
            >
              <Route path="/billing" exact component={BillingPage} />
            </Suspense>
          )}
          <Suspense fallback={<PageLoading>Loading your calendar</PageLoading>}>
            <EventFormProvider>
              <Route path="/calendar" exact component={CalendarPage} />
              <Route
                path="/calendar/new"
                exact
                component={CreateCalendarEventPage}
              />
              <Route
                path="/calendar/:id/edit"
                exact
                component={EditCalendarEventPage}
              />
            </EventFormProvider>
          </Suspense>
          <Suspense fallback={<PageLoading>Loading chat</PageLoading>}>
            <Route path="/chat" exact component={ChatPage} />
          </Suspense>
        </div>
        {/* Room for the mobile navigation bar. */}
        <div className="h-[calc(4rem+env(safe-area-inset-bottom))] md:h-0"></div>
      </div>
    </>
  );
}
